import { motion } from "framer-motion";
import { getIndustryReads } from "../data/industryReads";
import { getNewsArticles } from "../data/newsArticles";
import { useLocale } from "../i18n/LocaleContext";
import { buildLocalizedPath } from "../lib/siteRouting";

export function InsightsSection() {
  const { copy, locale } = useLocale();
  const { insights } = copy;
  const isChinese = locale === "zh" || locale === "vi";
  const items = getNewsArticles(locale);
  const industryReads = getIndustryReads(locale).slice(0, 3);
  const [leadItem, ...secondaryItems] = items;
  const industryLabel =
    locale === "zh"
      ? "行业观察"
      : locale === "ko"
        ? "산업 관찰"
        : locale === "vi"
          ? "Góc nhìn ngành"
          : "Industry Watch";
  const externalLabel =
    locale === "zh"
      ? "公开来源"
      : locale === "ko"
        ? "공개 출처"
        : locale === "vi"
          ? "Nguồn công khai"
          : "Public Source";

  return (
    <section id="insights" className="px-5 py-20 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-[15rem]">
            <p className="text-[0.72rem] uppercase tracking-[0.44em] text-aurora/78">{insights.eyebrow}</p>
            <h2
              className={`mt-4 leading-[1.08] text-[#1f2b31] ${
                isChinese
                  ? "font-sans text-[1.92rem] font-medium tracking-[-0.01em] sm:text-[2.55rem]"
                  : "font-display text-[2.15rem] font-semibold sm:text-[3.4rem]"
              }`}
            >
              {insights.title}
            </h2>
          </div>
          <a
            href="mailto:service@qiandu.group?subject=QIANDU%20Company%20Profile%20Request"
            className="inline-flex rounded-full border border-white/45 bg-[rgba(248,252,254,0.8)] px-5 py-3 text-[10px] uppercase tracking-[0.3em] text-[#1f2b31] shadow-[0_8px_18px_rgba(106,138,151,0.05)] transition hover:text-[#6f9cab]"
          >
            {insights.cta}
          </a>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.55 }}
            className="rounded-[2.5rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.9),rgba(240,246,249,0.94))] p-4 shadow-[0_10px_20px_rgba(106,138,151,0.05)] backdrop-blur sm:p-5"
          >
            <a href={buildLocalizedPath(locale, `/news/${leadItem.slug}`)} className="block">
              <div className="overflow-hidden rounded-[1.7rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]">
                <img
                  src={leadItem.image}
                  alt={leadItem.title}
                  className={`h-64 w-full sm:h-[26rem] ${leadItem.imageFit === "contain" ? "object-contain bg-[rgba(246,249,250,0.92)] p-3 sm:p-4" : "object-cover object-center"}`}
                />
              </div>
              <div className="mt-5 flex items-center justify-between gap-4">
                <div className="inline-flex rounded-full bg-[rgba(239,248,251,0.82)] px-3 py-1 text-[8px] uppercase tracking-[0.3em] text-[#6f8d99]">
                  {leadItem.tag}
                </div>
                <div className="text-[8px] uppercase tracking-[0.3em] text-soft/44">{leadItem.date}</div>
              </div>
              <h3 className={`mt-4 max-w-[32rem] leading-tight text-[#1f2b31] ${isChinese ? "font-sans text-[1.22rem] font-medium tracking-[-0.01em] sm:text-[1.4rem]" : "font-display text-[1.36rem] sm:text-[1.54rem]"}`}>
                {leadItem.title}
              </h3>
              <p className="mt-4 max-w-[27rem] text-[0.88rem] leading-7 text-soft/58">
                {leadItem.summary}
              </p>
            </a>
          </motion.article>

          <div className="grid gap-4">
            {secondaryItems.map((item, index) => (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: index * 0.08 }}
                className="rounded-[2.2rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.88),rgba(240,246,249,0.92))] p-4 shadow-[0_8px_18px_rgba(106,138,151,0.045)] backdrop-blur transition hover:-translate-y-1 hover:shadow-[0_12px_22px_rgba(106,138,151,0.065)] sm:p-5"
              >
                <a href={buildLocalizedPath(locale, `/news/${item.slug}`)} className="grid gap-4 sm:grid-cols-[0.9fr_1.1fr] sm:items-center">
                  <div className="overflow-hidden rounded-[1.3rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]">
                    <img
                      src={item.image}
                      alt={item.title}
                      className={`h-40 w-full ${item.imageFit === "contain" ? "object-contain bg-[rgba(246,249,250,0.92)] p-2" : "object-cover object-center"}`}
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-4">
                      <div className="inline-flex rounded-full bg-[rgba(239,248,251,0.82)] px-3 py-1 text-[8px] uppercase tracking-[0.3em] text-[#6f8d99]">
                        {item.tag}
                      </div>
                      <div className="text-[8px] uppercase tracking-[0.3em] text-soft/44">{item.date}</div>
                    </div>
                    <h3 className={`mt-4 min-h-[3rem] leading-tight text-[#1f2b31] ${isChinese ? "font-sans text-[0.96rem] font-medium tracking-[-0.01em]" : "font-display text-[1.04rem]"}`}>
                      {item.title}
                    </h3>
                    <p className="mt-3 text-[0.82rem] leading-6 text-soft/58">
                      {item.summary}
                    </p>
                  </div>
                </a>
              </motion.article>
            ))}
          </div>
        </div>

        {industryReads.length ? (
          <div className="mt-4 rounded-[2.4rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.86),rgba(240,246,249,0.92))] p-5 shadow-[0_8px_18px_rgba(106,138,151,0.045)] sm:p-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-[8px] uppercase tracking-[0.4em] text-aurora/72">{industryLabel}</div>
                <p className="mt-3 max-w-[34rem] text-[0.84rem] leading-6 text-soft/56">
                  {locale === "zh"
                    ? "同步公开行业报道与品牌科技趋势，补充公司新闻之外的市场观察。"
                    : locale === "ko"
                      ? "기업 뉴스와 함께 공개 산업 보도 및 뷰티 테크 흐름을 함께 제공합니다."
                      : locale === "vi"
                        ? "Bổ sung góc nhìn thị trường bằng các bài viết công khai về xu hướng K-Beauty và beauty-tech."
                        : "Public reporting and beauty-tech reads that complement QIANDU's own company updates."}
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              {industryReads.map((item, index) => (
                <motion.a
                  key={`${item.source}-${item.url}`}
                  href={buildLocalizedPath(locale, `/industry/${item.slug}`)}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: index * 0.06 }}
                  className="rounded-[2rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.92),rgba(241,247,250,0.96))] p-5 shadow-[0_7px_16px_rgba(106,138,151,0.045)] transition hover:-translate-y-1 hover:shadow-[0_12px_22px_rgba(106,138,151,0.065)]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="inline-flex rounded-full bg-[rgba(239,248,251,0.82)] px-3 py-1 text-[8px] uppercase tracking-[0.3em] text-[#6f8d99]">
                      {item.source}
                    </div>
                    <div className="text-[8px] uppercase tracking-[0.3em] text-soft/44">{item.date}</div>
                  </div>
                  <h3 className={`mt-4 leading-tight text-[#1f2b31] ${isChinese ? "font-sans text-[0.96rem] font-medium tracking-[-0.01em]" : "font-display text-[1.02rem]"}`}>
                    {item.title}
                  </h3>
                  <p className="mt-3 text-[0.84rem] leading-6 text-soft/58">{item.summary}</p>
                  <div className="mt-5 text-[8px] uppercase tracking-[0.34em] text-aurora/72">{externalLabel}</div>
                </motion.a>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
